import Player from '@smart-tv/components/player/player';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import useVodPlayer from '../_hooks/use-vod-player';
import Portal from '@smart-tv/components/portal/portal';
import useStream from '@smart-tv/components/player/_hooks/use-stream';
import useControls from '@smart-tv/components/player/_hooks/use-controls';
import { PlayerEvent, SeekableRange, StreamParams } from '@smart-tv/components/player/types';
import usePlayerEvent from '@smart-tv/components/player/_hooks/use-player-event';
import { useAtom, useAtomValue } from 'jotai';
import { descalePlayerAtom } from '../../atoms';
import PlayerProductSheetInfo from '../../player-product-sheet-info/player-product-sheet-info';
import { usePlayerContext } from '@smart-tv/components/player/_contexts/player-context';
import { LiveTvChannel, StreamType } from '@smart-tv/typings/content';
import useFavoritesLists from '@smart-tv/hooks/use-favorites-lists';
import { getEpgDataNowNextForChannels } from '@smart-tv/utils/kmm/getEpgDataNowNextForChannels';
import useInterval from '@smart-tv/hooks/use-interval';
import { networkError, BaseError } from '@smart-tv/contexts/error-context/error-context';
import useSuspendStartOverCancel from '@smart-tv/hooks/use-suspend-start-over-cancel';
import LivePlayerScreenInfo from './live-player-screen-info';
import PlayerBackButton from '../../player-back-button';
import usePlayer from '@smart-tv/components/player/use-player';
import styles from './live-player-screen.module.scss';

export const LivePlayerScreen: React.FC = () => {
  const { streamOptions, handleOpen } = useVodPlayer();
  const { handlePlayerRoutingOnClose } = usePlayer({});
  const { player, setIsPlaying, onSetChannels } = usePlayerContext();
  const { streamType, setError: setStreamError } = useStream();
  const { selectedFavoritesList } = useFavoritesLists();
  const { suspended, setSuspended, setSuspendedCallback } = useSuspendStartOverCancel();
  const descalePlayer = useAtomValue(descalePlayerAtom);
  const [, setDescalePlayer] = useAtom(descalePlayerAtom);
  const [channels, setChannels] = useState<LiveTvChannel[]>();
  const [selectedChannel, setSelectedChannel] = useState<LiveTvChannel>();
  const [streamParams, setStreamParams] = useState<StreamParams>();
  const [liveStreamPaused, setLiveStreamPaused] = useState(false);
  const [getChannelsInterval, setGetChannelsInterval] = useState(600000);
  const seekableRangeRef = useRef<SeekableRange | null>(null);
  const sid = streamOptions?.streamParams?.sid;
  const eventId = streamOptions?.streamParams?.eventId || selectedChannel?.currentProgram?.eventId;

  // Fetch channels
  const getChannels = useCallback(
    async (favoriteList: number) => {
      try {
        const data = await getEpgDataNowNextForChannels({ favoriteList });
        if (data) {
          const { channels, updateNextInSec } = data;
          setChannels(channels);
          onSetChannels(channels);
          setGetChannelsInterval(updateNextInSec * 1000);
        }
      } catch (error) {
        if (error === networkError || (error as BaseError).offline) {
          setStreamError(networkError);
        }
      }
    },
    [onSetChannels, setStreamError]
  );

  // Fetch channels on mount and when favorites list changes
  useEffect(() => {
    if (selectedFavoritesList) {
      getChannels(selectedFavoritesList.id);
    }
  }, [selectedFavoritesList, getChannels]);

  // Update selected channel when channels or sid changes
  useEffect(() => {
    if (!channels?.length || (streamType === StreamType.StartOver && selectedChannel)) return;

    const sidChannel = sid && channels.find((channel) => channel.sid === sid);
    if (sidChannel) {
      setSelectedChannel(sidChannel);
    } else if (channels[0]) {
      setSelectedChannel(channels[0]);
    }
  }, [channels, sid, streamType, selectedChannel]);

  // Refresh channels at interval for live streams
  useInterval(
    () =>
      selectedFavoritesList &&
      streamType === StreamType.LiveStream &&
      getChannels(selectedFavoritesList.id),
    getChannelsInterval
  );

  useEffect(() => {
    if (!streamType || !selectedChannel?.sid) return;

    const params: StreamParams = { sid: selectedChannel.sid };

    if (streamType === StreamType.StartOver && eventId) {
      params.eventId = eventId;
    }

    const optionsProgress = streamOptions?.streamParams?.progress;
    if (streamType === StreamType.StartOver && optionsProgress !== undefined) {
      params.progress = optionsProgress;
    }

    setStreamParams(params);
  }, [selectedChannel?.sid, eventId, streamType, streamOptions?.streamParams?.progress]);

  const playerControls = useControls();

  const handleBeginStartOver = useCallback(
    (initialProgress?: number) => {
      if (!streamOptions || !selectedChannel?.sid) return;

      player?.pause();
      setSuspended(true);
      handleOpen({
        ...streamOptions,
        streamType: StreamType.StartOver,
        streamParams: {
          sid: selectedChannel.sid,
          ...(eventId && { eventId }),
          ...(initialProgress !== undefined && { progress: initialProgress })
        }
      });
    },
    [player, setSuspended, handleOpen, streamOptions, selectedChannel?.sid, eventId]
  );

  const handleCancelStartOver = useCallback(() => {
    if (!streamOptions || !selectedChannel?.sid) return;

    const run = () =>
      handleOpen({
        ...streamOptions,
        streamType: StreamType.LiveStream,
        streamParams: { sid: selectedChannel.sid }
      });
    suspended ? setSuspendedCallback({ run, type: 'Cancel start over button' }) : run();
  }, [suspended, setSuspendedCallback, handleOpen, streamOptions, selectedChannel?.sid]);

  const handlePlay = useCallback(() => {
    setIsPlaying(true);
  }, [setIsPlaying]);

  const handlePause = useCallback(() => {
    if (streamType === StreamType.LiveStream) {
      const seekable = seekableRangeRef.current;
      const liveProgress = seekable ? Math.max(0, Math.floor(seekable.range.end)) : undefined;
      handleBeginStartOver(liveProgress);
      setLiveStreamPaused(true);
    } else {
      player?.pause();
      setIsPlaying(false);
    }
  }, [streamType, handleBeginStartOver, player, setIsPlaying]);

  // Set up player control handlers
  useEffect(() => {
    playerControls.setOnPause(() => handlePause);
    playerControls.setOnPlay(() => handlePlay);
    playerControls.setHandleBeginStartOver(() => handleBeginStartOver);
    playerControls.setHandleCancelStartOver(() => handleCancelStartOver);
  }, [handlePause, handlePlay, handleBeginStartOver, handleCancelStartOver, playerControls]);

  const handlePlayCompleted = useCallback(() => {
    if (streamType === StreamType.StartOver && streamOptions && selectedChannel?.sid) {
      setSuspended(false);
      setSuspendedCallback(null);
      handleOpen({
        ...streamOptions,
        streamType: StreamType.LiveStream,
        streamParams: { sid: selectedChannel.sid }
      });
    }
  }, [
    handleOpen,
    streamOptions,
    selectedChannel?.sid,
    setSuspended,
    setSuspendedCallback,
    streamType
  ]);

  const handlePlaytimeUpdated = useCallback(
    async (seekableRange: SeekableRange) => {
      if (seekableRangeRef.current) {
        const prevSeekableRange = seekableRangeRef.current;
        const { position: prevPosition, range: prevRange, live: prevLive } = prevSeekableRange;
        const { range, live } = seekableRange;

        if (
          prevPosition === 0 &&
          streamType === StreamType.StartOver &&
          range?.start >= 0 &&
          range?.end > 0
        ) {
          if (liveStreamPaused) {
            player?.pause();
            setIsPlaying(false);
            setLiveStreamPaused(false);
          }
        }
      }
      seekableRangeRef.current = seekableRange;
    },
    [streamType, liveStreamPaused, player, setIsPlaying]
  );

  const handleBack = useCallback(() => {
    descalePlayer ? setDescalePlayer(false) : handlePlayerRoutingOnClose();
  }, [descalePlayer, handlePlayerRoutingOnClose, setDescalePlayer]);

  usePlayerEvent(PlayerEvent.PlayCompleted, handlePlayCompleted);
  usePlayerEvent(PlayerEvent.PlayTimeUpdated, (e: SeekableRange) => handlePlaytimeUpdated(e));

  if (!streamOptions) return null;

  const rootClass = clsx(styles['live-player-screen'], {
    [styles['live-player-screen--descaled']]: descalePlayer
  });

  return (
    <Portal>
      <Player streamParams={streamParams} className={rootClass}>
        <PlayerBackButton onClick={handleBack} />
        <LivePlayerScreenInfo selectedChannel={selectedChannel} />
      </Player>
      {descalePlayer && <PlayerProductSheetInfo sid={sid} eventId={eventId} />}
    </Portal>
  );
};

export default React.memo(LivePlayerScreen);
